/**
 * API · core.messages + reactions + mentions
 */
import { supabase } from '@/lib/supabase';
import type {
  MentionsRow,
  MessageReactionsRow,
  MessagesInsert,
  MessagesRow,
} from '@/types/database.types';

// Casts a any: las tablas chat NO están en database.types.ts hand-curated.
// Los tipos vienen de nuestro database.types.ts (hand-curated también).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface ListMessagesOptions {
  before?: string; // created_at cursor, descendente
  limit?: number;  // default 50
}

export async function listMessages(
  channelId: string,
  options: ListMessagesOptions = {},
): Promise<MessagesRow[]> {
  let q = db
    .from('messages')
    .select('*')
    .eq('channel_id', channelId)
    .is('thread_root_id', null)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(options.limit ?? 50);

  if (options.before) q = q.lt('created_at', options.before);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as MessagesRow[]).reverse();
}

export async function getMessage(id: string): Promise<MessagesRow | null> {
  const { data, error } = await db
    .from('messages')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as MessagesRow | null) ?? null;
}

export async function listThread(rootId: string): Promise<MessagesRow[]> {
  const { data, error } = await db
    .from('messages')
    .select('*')
    .or(`id.eq.${rootId},thread_root_id.eq.${rootId}`)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as MessagesRow[];
}

export async function sendMessage(input: MessagesInsert): Promise<MessagesRow> {
  const { data, error } = await db
    .from('messages')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data as MessagesRow;
}

export async function editMessage(id: string, body: string): Promise<void> {
  const { error } = await db
    .from('messages')
    .update({ body, edited_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function softDeleteMessage(id: string): Promise<void> {
  const { error } = await db
    .from('messages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// =====================
// Reactions
// =====================

export async function listReactions(messageIds: string[]): Promise<MessageReactionsRow[]> {
  if (messageIds.length === 0) return [];
  const { data, error } = await db
    .from('message_reactions')
    .select('*')
    .in('message_id', messageIds);
  if (error) throw error;
  return (data ?? []) as MessageReactionsRow[];
}

export async function toggleReaction(
  messageId: string,
  userId: string,
  emoji: string,
): Promise<'added' | 'removed'> {
  const { error: insErr } = await db
    .from('message_reactions')
    .insert({ message_id: messageId, user_id: userId, emoji });
  if (!insErr) return 'added';
  const { error: delErr } = await db
    .from('message_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji);
  if (delErr) throw delErr;
  return 'removed';
}

// =====================
// Thread counts
// =====================

export interface ThreadCount {
  root_id: string;
  count: number;
  last_at: string;
}

/**
 * Conteo de replies por thread root dentro de un canal.
 * Sirve para mostrar el chip 'N respuestas · última X' en cada mensaje raíz
 * de la timeline sin tener que cargar todos los hijos.
 *
 * Estrategia: SELECT directo sobre core.messages agrupando por
 * thread_root_id. Si la cantidad de threads en un canal crece (>500),
 * conviene moverlo a un materialized view o a una columna agregada en
 * messages.
 */
export async function listThreadCounts(channelId: string): Promise<ThreadCount[]> {
  const { data, error } = await db
    .from('messages')
    .select('thread_root_id, created_at')
    .eq('channel_id', channelId)
    .not('thread_root_id', 'is', null)
    .is('deleted_at', null);
  if (error) throw error;
  const map = new Map<string, ThreadCount>();
  for (const row of (data ?? []) as { thread_root_id: string; created_at: string }[]) {
    const cur = map.get(row.thread_root_id);
    if (!cur) {
      map.set(row.thread_root_id, {
        root_id: row.thread_root_id,
        count: 1,
        last_at: row.created_at,
      });
    } else {
      cur.count += 1;
      if (row.created_at > cur.last_at) cur.last_at = row.created_at;
    }
  }
  return [...map.values()];
}

/**
 * Thread roots en los que el usuario participó (mandó al menos un reply).
 * Alimenta la vista "Threads" del rail. RLS limita a canales visibles.
 * v1: "conversaciones en las que participaste" — no incluye threads que
 * arrancaste pero a los que nadie (ni vos) respondió.
 */
export async function listMyThreadRoots(userId: string): Promise<MessagesRow[]> {
  const { data: replies, error } = await db
    .from('messages')
    .select('thread_root_id')
    .eq('user_id', userId)
    .not('thread_root_id', 'is', null)
    .is('deleted_at', null);
  if (error) throw error;
  const rootIds = [
    ...new Set(
      ((replies ?? []) as { thread_root_id: string }[]).map((r) => r.thread_root_id),
    ),
  ];
  if (rootIds.length === 0) return [];
  const { data: roots, error: rErr } = await db
    .from('messages')
    .select('*')
    .in('id', rootIds)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (rErr) throw rErr;
  return (roots ?? []) as MessagesRow[];
}

// =====================
// Mentions
// =====================

export async function listMyUnreadMentions(): Promise<MentionsRow[]> {
  const { data, error } = await db
    .from('mentions')
    .select('*')
    .is('read_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as MentionsRow[];
}

export async function markMentionRead(id: string): Promise<void> {
  const { error } = await db
    .from('mentions')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllMentionsRead(): Promise<void> {
  const { error } = await db
    .from('mentions')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);
  if (error) throw error;
}

/**
 * Marca leídas las menciones de UN canal (al entrar al chat se asume leído).
 * Filtra por `context_url` (formato '/chat?channel=<id>&msg=<id>'). RLS acota al
 * propio usuario (mentioned_user_id = auth.uid()).
 */
export async function markChannelMentionsRead(channelId: string): Promise<void> {
  const { error } = await db
    .from('mentions')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null)
    .like('context_url', `%channel=${channelId}%`);
  if (error) throw error;
}
